import Button from '@mui/material/Button';
import { useState } from 'react';
import { Box } from '@/component/Box';
import ButtonLoading from '@/component/ButtonLoading';
import { ToastType, useToast } from '@/component/Toast';
import Tooltip from '@/component/Tooltip';
import Translate from '@/component/Translate';
import { useSignAgreementMutation } from '@/feature/agreement/mutations';
import useAuth from '@/feature/auth/useAuth';
import useTranslation from '@/feature/lang/hook';
import { AgreementDialog } from './dialog/AgreementDialog';
import { useAcknowledgements } from './dialog/useAcknowledgements';

interface SignUserAgreementDialogProps {
  signActions: SignDocumentAction[];
  onDone: () => void;
}

const SignUserAgreementDialog = ({
  signActions,
  onDone,
}: SignUserAgreementDialogProps) => {
  const { texts } = useTranslation();
  const toast = useToast();
  const { mutateAsync: signAgreement, isPending } = useSignAgreementMutation();
  const documentIds = signActions.map((action) => action.id);
  const { logout } = useAuth();
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const { allChecked } = useAcknowledgements(documentIds);
  const acceptEnabled = scrolledToEnd && allChecked;

  function handleSign() {
    signAgreement(documentIds)
      .then(onDone)
      .catch((error) =>
        toast({
          message: texts.agreement.notification.signFailed,
          values: { message: error?.message },
          type: ToastType.Error,
        })
      );
  }

  return (
    <AgreementDialog
      open
      documentIds={documentIds}
      onScrolledToEnd={() => setScrolledToEnd(true)}
      actions={
        <Box display="flex" gap={2} alignItems={'end'}>
          <Button onClick={logout} variant="outlined">
            <Translate text={texts.user.action.logout} />
          </Button>
          <Tooltip
            title={acceptEnabled ? '' : texts.agreement.action.accept.tooltip}
          >
            <span>
              <ButtonLoading
                type="button"
                color="primary"
                variant="contained"
                onClick={handleSign}
                loading={isPending}
                disabled={!acceptEnabled}
              >
                <Translate text={texts.agreement.action.accept} />
              </ButtonLoading>
            </span>
          </Tooltip>
        </Box>
      }
    ></AgreementDialog>
  );
};

export default SignUserAgreementDialog;
